import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import useRole from '../../hooks/useRole';
import { db, storage } from '../../firebase/config';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import PageHeader from '../../components/common/PageHeader';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import { HiOutlinePencilSquare, HiOutlineLockClosed, HiOutlineBell, HiOutlineDocumentText, HiOutlineShieldCheck, HiOutlineCamera, HiOutlineTrash } from 'react-icons/hi2';
import { HiOutlineUser } from 'react-icons/hi2';
import { IoLogOutOutline } from 'react-icons/io5';
import { deleteDoc, doc as firestoreDoc } from 'firebase/firestore';
import { MdCheckBox, MdPeople } from 'react-icons/md';
import { friendlyTimestamp } from '../../utils/dateFormat';

const ProfilePage = () => {
  const { userData, currentUser, logout } = useAuth();
  const { role, isStudent, isParent } = useRole();
  const isChildView = isStudent || isParent;
  const navigate = useNavigate();

  const [stats, setStats] = useState({ classes: 0, students: 0, attRate: 0, submitRate: 0, avgScore: 0 });
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!userData?.uid) return;
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.uid]);

  const loadStats = async () => {
    try {
      const uid = userData.uid;
      if (isChildView) {
        // 학생/학부모 통계
        const lookupUid = isParent ? (userData.children?.[0] || uid) : uid;
        const classQ = query(collection(db, 'classes'), where('studentIds', 'array-contains', lookupUid));
        const classSnap = await getDocs(classQ);
        const classList = classSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        let attTotal = 0, attPresent = 0, assignTotal = 0, assignSubmitted = 0;
        const scores = [];

        for (const cls of classList) {
          const [attSnap, assignSnap, gradeSnap] = await Promise.all([
            getDocs(collection(db, 'classes', cls.id, 'attendance')),
            getDocs(collection(db, 'classes', cls.id, 'assignments')),
            getDocs(collection(db, 'classes', cls.id, 'grades')),
          ]);
          attSnap.docs.forEach(d => {
            const r = (d.data().records || {})[lookupUid];
            if (r) {
              attTotal++;
              const status = typeof r === 'string' ? r : r.status;
              if (status === 'present' || status === 'late') attPresent++;
            }
          });
          assignSnap.docs.forEach(d => {
            assignTotal++;
            const sub = (d.data().submissions || {})[lookupUid];
            if (sub?.submitted) assignSubmitted++;
          });
          gradeSnap.docs.forEach(d => {
            const s = (d.data().scores || {})[lookupUid];
            if (s !== undefined) scores.push(s);
          });
        }

        setStats({
          classes: classList.length,
          students: 0,
          attRate: attTotal > 0 ? Math.round((attPresent / attTotal) * 100) : 0,
          submitRate: assignTotal > 0 ? Math.round((assignSubmitted / assignTotal) * 100) : 0,
          avgScore: scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0,
        });
      } else {
        // 강사 통계
        const classQ = query(collection(db, 'classes'), where('teacherId', '==', uid));
        const classSnap = await getDocs(classQ);
        const classes = classSnap.docs.map(d => d.data());
        const totalStudents = classes.reduce((sum, c) => sum + (c.studentIds?.length || 0), 0);
        setStats({ classes: classes.length, students: totalStudents, attRate: 0, submitRate: 0, avgScore: 0 });
      }
    } catch (err) {
      console.error('통계 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleEditProfile = async () => {
    if (!userData?.uid) return;
    try {
      const updates = {};
      if (editForm.name) updates.name = editForm.name;
      if (editForm.phone) updates.phone = editForm.phone;
      await updateDoc(doc(db, 'users', userData.uid), updates);
      setShowEditModal(false);
      window.location.reload();
    } catch (err) {
      console.error('프로필 수정 실패:', err);
    }
  };

  const handleChangePassword = async () => {
    setPwError('');
    if (!pwForm.current || !pwForm.newPw || !pwForm.confirm) {
      setPwError('모든 항목을 입력해주세요.');
      return;
    }
    if (pwForm.newPw.length < 6) {
      setPwError('새 비밀번호는 6자 이상이어야 합니다.');
      return;
    }
    if (pwForm.newPw !== pwForm.confirm) {
      setPwError('새 비밀번호가 일치하지 않습니다.');
      return;
    }
    try {
      const credential = EmailAuthProvider.credential(currentUser.email, pwForm.current);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, pwForm.newPw);
      alert('비밀번호가 변경되었습니다.');
      setShowPasswordModal(false);
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setPwError('현재 비밀번호가 올바르지 않습니다.');
      } else {
        setPwError('비밀번호 변경에 실패했습니다.');
      }
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !userData?.uid) return;
    if (!file.type.startsWith('image/')) return;
    setAvatarUploading(true);
    try {
      const storageRef = ref(storage, `avatars/${userData.uid}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await updateDoc(doc(db, 'users', userData.uid), { photoURL: url });
      window.location.reload();
    } catch (err) {
      console.error('아바타 업로드 실패:', err);
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== '회원탈퇴') return;
    setDeleting(true);
    try {
      // Firestore 사용자 문서 삭제
      if (userData?.uid) {
        await deleteDoc(firestoreDoc(db, 'users', userData.uid));
        // phones 인덱스 삭제
        try { await deleteDoc(firestoreDoc(db, 'phones', userData.uid)); } catch {}
      }
      // Firebase Auth 계정 삭제
      if (currentUser) {
        await currentUser.delete();
      }
      navigate('/login');
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        alert('보안을 위해 다시 로그인한 후 탈퇴를 진행해주세요.');
        await logout();
        navigate('/login');
      } else {
        alert('회원 탈퇴에 실패했습니다. 다시 시도해주세요.');
        console.error('회원탈퇴 실패:', err);
      }
    } finally {
      setDeleting(false);
    }
  };

  const roleLabel = role === 'parent' ? '학부모' : role === 'assistant' ? '조교' : role === 'teacher' ? '강사' : role === 'student' ? '학생' : (role || '강사');

  const formatDate = (ts) => friendlyTimestamp(ts);

  return (
    <Container>
      <PageHeader title="프로필" showBack={false} />

      <Content>
        <ProfileCard>
          <ProfileTopRow>
            <AvatarWrap>
              {userData?.photoURL ? (
                <AvatarImg src={userData.photoURL} alt="프로필" />
              ) : (
                <AvatarDefault><HiOutlineUser size={30} /></AvatarDefault>
              )}
              {avatarUploading && (
                <AvatarSpinnerOverlay>
                  <AvatarSpinner />
                </AvatarSpinnerOverlay>
              )}
              <AvatarCameraBtn as="label" htmlFor="avatar-upload">
                <HiOutlineCamera size={14} />
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleAvatarChange}
                  disabled={avatarUploading}
                />
              </AvatarCameraBtn>
            </AvatarWrap>
            <ProfileInfo>
              <ProfileNameRow>
                <ProfileName>{userData?.name || '사용자'}</ProfileName>
                <RoleBadge>{roleLabel}</RoleBadge>
              </ProfileNameRow>
            </ProfileInfo>
            <ProfileEditBtn onClick={() => {
              setEditForm({ name: userData?.name || '', phone: userData?.phone || '' });
              setShowEditModal(true);
            }}>수정하기</ProfileEditBtn>
          </ProfileTopRow>
          <ProfileDetailRow>
            {userData?.email && <ProfileDetailItem>{userData.email}</ProfileDetailItem>}
            {userData?.phone && <ProfileDetailItem>{userData.phone}</ProfileDetailItem>}
            {userData?.createdAt && <ProfileDetailItem>가입일: {formatDate(userData.createdAt)}</ProfileDetailItem>}
          </ProfileDetailRow>
        </ProfileCard>



        <MenuSection>
          <MenuTitle>계정</MenuTitle>
          <MenuItem onClick={() => {
            setEditForm({ name: userData?.name || '', phone: userData?.phone || '' });
            setShowEditModal(true);
          }}>
            <MenuIconPlain><HiOutlinePencilSquare size={20} /></MenuIconPlain>
            <MenuLabel>프로필 수정</MenuLabel>
            <MenuArrow>&gt;</MenuArrow>
          </MenuItem>
          <MenuItem onClick={() => {
            const hasPassword = currentUser?.providerData?.some(p => p.providerId === 'password');
            if (!hasPassword) {
              alert('소셜 로그인 가입자는 비밀번호를 변경할 수 없습니다.');
              return;
            }
            setShowPasswordModal(true);
          }}>
            <MenuIconPlain><HiOutlineLockClosed size={20} /></MenuIconPlain>
            <MenuLabel>비밀번호 변경</MenuLabel>
            <MenuArrow>&gt;</MenuArrow>
          </MenuItem>
          <MenuItem onClick={() => navigate('/notification-settings')}>
            <MenuIconPlain><HiOutlineBell size={20} /></MenuIconPlain>
            <MenuLabel>알림 설정</MenuLabel>
            <MenuArrow>&gt;</MenuArrow>
          </MenuItem>
          <MenuItem onClick={() => setShowDeleteModal(true)}>
            <MenuIconPlain style={{ color: '#EF4444' }}><HiOutlineTrash size={20} /></MenuIconPlain>
            <MenuLabel style={{ color: '#EF4444' }}>회원탈퇴</MenuLabel>
            <MenuArrow>&gt;</MenuArrow>
          </MenuItem>
        </MenuSection>

        <MenuSection>
          <MenuTitle>기타</MenuTitle>
          <MenuItem onClick={() => navigate('/terms')}>
            <MenuIconPlain><HiOutlineDocumentText size={20} /></MenuIconPlain>
            <MenuLabel>이용약관</MenuLabel>
            <MenuArrow>&gt;</MenuArrow>
          </MenuItem>
          <MenuItem onClick={() => navigate('/privacy')}>
            <MenuIconPlain><HiOutlineShieldCheck size={20} /></MenuIconPlain>
            <MenuLabel>개인정보 처리지침</MenuLabel>
            <MenuArrow>&gt;</MenuArrow>
          </MenuItem>
        </MenuSection>

        <LogoutButton onClick={handleLogout}>
          로그아웃
        </LogoutButton>

        <StoreInfoSection>
          <StoreName>ClassCare</StoreName>
          <StoreLinkRow>
            <StoreLink onClick={() => navigate('/terms')}>이용약관</StoreLink>
            <StoreLink onClick={() => navigate('/privacy')}>개인정보 처리지침</StoreLink>
          </StoreLinkRow>
          <StoreBusinessText>상호명 : 윤수빈 영어 연구소 | 대표자명 : 윤수빈</StoreBusinessText>
          <StoreBusinessText>전자우편주소 : kkan2222@gmail.com</StoreBusinessText>
          <StoreBusinessText>사업자등록번호 : 480-86-03245</StoreBusinessText>
          <StoreBusinessText>주소 : 서울특별시 서초구 사임당로8길 13, 4층</StoreBusinessText>
          <StoreNotice>
            윤수빈 영어 연구소는 교육 서비스 제공자로서, 수업 및 학습 관련 서비스의 품질 향상을 위해 최선을 다하고 있습니다.
          </StoreNotice>
        </StoreInfoSection>
      </Content>

      {showEditModal && (
        <ModalOverlay onClick={() => setShowEditModal(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>프로필 수정</ModalTitle>
              <CloseBtn onClick={() => setShowEditModal(false)}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <FieldLabel>이름</FieldLabel>
              <FieldInput
                value={editForm.name || ''}
                onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                placeholder=""
              />
              <FieldLabel>전화번호</FieldLabel>
              <FieldInput
                value={editForm.phone || ''}
                onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))}
                placeholder=""
              />
              <ModalButtons>
                <CancelBtn onClick={() => setShowEditModal(false)}>취소</CancelBtn>
                <SubmitBtn onClick={handleEditProfile}>저장</SubmitBtn>
              </ModalButtons>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
      {showPasswordModal && (
        <ModalOverlay onClick={() => { setShowPasswordModal(false); setPwError(''); }}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>비밀번호 변경</ModalTitle>
              <CloseBtn onClick={() => { setShowPasswordModal(false); setPwError(''); }}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <FieldLabel>현재 비밀번호</FieldLabel>
              <FieldInput
                type="password"
                value={pwForm.current}
                onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))}
                placeholder=""
              />
              <FieldLabel>새 비밀번호</FieldLabel>
              <FieldInput
                type="password"
                value={pwForm.newPw}
                onChange={e => setPwForm(p => ({ ...p, newPw: e.target.value }))}
                placeholder=""
              />
              <FieldLabel>새 비밀번호 확인</FieldLabel>
              <FieldInput
                type="password"
                value={pwForm.confirm}
                onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))}
                placeholder=""
              />
              {pwError && <PwError>{pwError}</PwError>}
              <ModalButtons>
                <CancelBtn onClick={() => { setShowPasswordModal(false); setPwError(''); }}>취소</CancelBtn>
                <SubmitBtn onClick={handleChangePassword}>변경</SubmitBtn>
              </ModalButtons>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
      {showDeleteModal && (
        <ModalOverlay onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader style={{ background: '#FEF2F2' }}>
              <ModalTitle style={{ color: '#DC2626' }}>회원탈퇴</ModalTitle>
              <CloseBtn onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <DeleteWarning>
                탈퇴하시면 모든 데이터가 삭제되며 복구할 수 없습니다.
              </DeleteWarning>
              <FieldLabel>확인을 위해 <strong>"회원탈퇴"</strong>를 입력해주세요.</FieldLabel>
              <FieldInput
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder="회원탈퇴"
              />
              <ModalButtons>
                <CancelBtn onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}>취소</CancelBtn>
                <DeleteBtn
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmText !== '회원탈퇴' || deleting}
                >
                  {deleting ? '처리중...' : '탈퇴하기'}
                </DeleteBtn>
              </ModalButtons>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default ProfilePage;

const Container = styled.div`
  background: ${({ theme }) => theme.colors.background.default};
  min-height: 100vh;
  padding-bottom: 80px;
`;

const Content = styled.div`
  padding: 16px;
`;

const ProfileCard = styled(Card)`
  padding: 16px;
  margin-bottom: 16px;
  background: ${({ theme }) => theme.colors.primaryLight};
`;

const ProfileTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AvatarWrap = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  flex-shrink: 0;
`;

const AvatarImg = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
`;

const AvatarDefault = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #E5E7EB;
  color: #9CA3AF;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AvatarSpinnerOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const AvatarSpinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: avatar-spin 0.8s linear infinite;
  @keyframes avatar-spin {
    to { transform: rotate(360deg); }
  }
`;

const AvatarCameraBtn = styled.button`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: white;
  border: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  cursor: pointer;
`;

const ProfileInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProfileNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProfileName = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
`;

const RoleBadge = styled.span`
  font-size: 12px;
  color: #6B7280;
  background: #F3F4F6;
  padding: 2px 8px;
  border-radius: 4px;
`;

const ProfileEditBtn = styled.button`
  font-size: 13px;
  color: #6B7280;
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: 4px;
  text-decoration: underline;
  text-underline-offset: 2px;
`;

const ProfileDetailRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(74, 124, 254, 0.15);
`;

const ProfileDetailItem = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 20px;
`;

const StatCard = styled(Card)`
  padding: 16px;
  background: ${({ $bg }) => $bg};
`;

const StatIconBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ $bg }) => $bg};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
`;

const StatValue = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xxl};
`;

const StatLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
`;

const MenuSection = styled(Card)`
  margin-bottom: 12px;
  padding: 8px 16px;
`;

const MenuTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.hint};
  padding: 8px 0;
`;

const MenuItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider || '#E0E0E0'};
  &:last-child { border: none; }
`;

const MenuIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ $bg }) => $bg};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MenuIconPlain = styled.div`
  color: #374151;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MenuLabel = styled.span`
  flex: 1;
  text-align: left;
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const MenuArrow = styled.span`
  color: ${({ theme }) => theme.colors.text.hint};
`;

const LogoutButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: none;
  background: white;
  color: #1A1A2E;
  border: 1px solid #E5E7EB;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 600;
`;

const StoreInfoSection = styled.div`
  background: #F2F3F4;
  padding: 20px;
  margin-top: 16px;
  border-radius: 0;
`;

const StoreName = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: #111;
  margin-bottom: 10px;
`;

const StoreLinkRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const StoreLink = styled.span`
  font-size: 13px;
  color: #333;
  cursor: pointer;
`;

const StoreBusinessText = styled.p`
  font-size: 12px;
  color: #777;
  line-height: 1.6;
  word-break: keep-all;
`;

const StoreNotice = styled.p`
  font-size: 11px;
  color: #777;
  line-height: 1.5;
  margin-top: 14px;
  word-break: keep-all;
`;

// Modal
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 480px;
  background: white;
  border-radius: 20px 20px 0 0;
  max-height: 85vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: var(--color-primary-light);
  border-bottom: 1px solid #D9E2FF;
`;

const ModalTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 700;
  color: #1E3A5F;
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.text.primary};
  background: white;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-bottom: 6px;
  margin-top: 14px;
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const SubmitBtn = styled.button`
  flex: 2;
  padding: 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const PwError = styled.p`
  color: #EF4444;
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-top: 12px;
`;

const DeleteWarning = styled.p`
  color: #DC2626;
  font-size: ${({ theme }) => theme.fontSize.md};
  line-height: 1.6;
  margin-bottom: 16px;
  padding: 12px;
  background: #FEF2F2;
  border-radius: 8px;
`;

const DeleteBtn = styled.button`
  flex: 2;
  padding: 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  background: #DC2626;
  color: white;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 600;
  &:disabled { opacity: 0.4; }
`;
